import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { api } from '@frontend/api/client';
import type { ISODate } from '@shared/types';

export interface OnboardingStepView {
  id: string;
  key: string;
  title: string;
  description: string | null;
  sortOrder: number;
  required: boolean;
  blocksActivation: boolean;
  status: string;
  assignedTo: string;
  assignedToEmployeeId: string | null;
  documentType: string | null;
  automationAction: string | null;
  dueDate: ISODate | null;
  daysOverdue: number;
  completedAt: string | null;
  skipReason: string | null;
  notes: string | null;
  dependsOnKeys: string[];
  blockedBy: { key: string; title?: string; reason?: string }[];
  ready: boolean;
  documentIds: string[];
  documentVerified: boolean;
  signature: { envelopeId: string; status: string; signedAt: string | null } | null;
}

export interface ChecklistInstanceSummary {
  id: string;
  employeeId: string;
  kind: string;
  status: string;
  templateId: string | null;
  startedAt: string | null;
  dueDate: ISODate | null;
  completedAt: string | null;
}

export interface ChecklistView {
  instance: ChecklistInstanceSummary;
  steps: OnboardingStepView[];
  progress: {
    total: number;
    required: number;
    completed: number;
    skipped: number;
    outstanding: number;
    overdue: number;
    percentComplete: number;
  };
  blockingActivation: OnboardingStepView[];
}

export interface MyOnboarding {
  checklist: ChecklistView | null;
  message?: string;
  myNextSteps?: OnboardingStepView[];
  /** Flattened for the dashboard card. */
  instance?: ChecklistInstanceSummary | null;
  completedSteps?: number;
  totalSteps?: number;
}

export function useMyOnboarding(): UseQueryResult<MyOnboarding> {
  return useQuery({
    queryKey: ['onboarding', 'mine'],
    queryFn: async () => {
      const response = await api.get<MyOnboarding>('/onboarding/my');
      return {
        ...response,
        instance: response.checklist?.instance ?? null,
        completedSteps: response.checklist?.progress.completed ?? 0,
        totalSteps: response.checklist?.progress.total ?? 0,
      };
    },
    retry: false,
  });
}

export function useChecklist(instanceId: string | null): UseQueryResult<ChecklistView> {
  return useQuery({
    queryKey: ['onboarding', 'checklist', instanceId],
    queryFn: () => api.get<ChecklistView>(`/onboarding/${instanceId ?? ''}`),
    enabled: instanceId !== null && instanceId !== '',
  });
}

/**
 * A row in the active-checklist LIST — a FLAT checklist instance, not the rich
 * {@link ChecklistView} the detail endpoint returns.
 *
 * This distinction was the bug behind "no onboarding in progress": the list endpoint answers
 * flat instance rows (`id`, `employeeId`, `status`, `completedStepCount`…), but the screen
 * was typed against `ChecklistView` and read `row.instance.id` / `row.progress` / row-level
 * `blockingActivation` — none of which exist here. `row.instance` was `undefined`, so the
 * page threw on every row and the ErrorBoundary swallowed the whole screen. It only ever
 * *looked* empty because it crashed exactly when there was data to show. Per-checklist
 * overdue and blocking counts are NOT in this payload — the overdue-steps section below
 * carries that from its own query.
 */
export interface ChecklistListItem {
  id: string;
  employeeId: string;
  kind: string;
  status: string;
  startDate: ISODate | null;
  dueDate: ISODate | null;
  completedStepCount: number;
  totalStepCount: number;
  startedAt: string | null;
}

export function useActiveChecklists(kind?: 'ONBOARDING' | 'OFFBOARDING'): UseQueryResult<{
  checklists: ChecklistListItem[];
}> {
  return useQuery({
    queryKey: ['onboarding', 'active', kind ?? 'all'],
    queryFn: () => api.get<{ checklists: ChecklistListItem[] }>('/onboarding/active', { kind, openOnly: 'true' }),
    retry: false,
  });
}

export interface OverdueStep {
  checklistId: string;
  employeeId: string;
  kind: string;
  stepId: string;
  stepKey: string;
  title: string;
  assignedTo: string;
  dueDate: ISODate | null;
  daysOverdue: number;
  blocksActivation: boolean;
}

export function useOverdueOnboarding(): UseQueryResult<{
  overdue: OverdueStep[];
  total: number;
  blockingActivation: number;
}> {
  return useQuery({
    queryKey: ['onboarding', 'overdue'],
    queryFn: () =>
      api.get<{ overdue: OverdueStep[]; total: number; blockingActivation: number }>('/onboarding/overdue'),
    retry: false,
  });
}

export function useCompleteStep(
  instanceId: string | null,
): ReturnType<typeof useMutation<unknown, unknown, { stepId: string; notes?: string | null }>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { stepId: string; notes?: string | null }) =>
      api.post(`/onboarding/${instanceId ?? ''}/step/${input.stepId}/complete`, {
        notes: input.notes ?? null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

/**
 * START a checklist — the route the whole module depends on.
 *
 * Creating an employee does NOT open one: employee-core publishes a lifecycle event and
 * nothing subscribes to it for onboarding (termination, by contrast, opens an offboarding
 * checklist automatically). Until this hook existed there was no caller anywhere in the
 * frontend, so the entry point to the module was unreachable and every screen below it
 * showed an empty state that read as a bug.
 *
 * `templateId` is optional and normally omitted — the server resolves the template from the
 * employee's country and employment type, which is the behaviour worth relying on.
 */
export function useStartOnboarding(): ReturnType<
  typeof useMutation<unknown, unknown, { employeeId: string; templateId?: string | null; startDate?: string | null }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { employeeId: string; templateId?: string | null; startDate?: string | null }) =>
      api.post(`/onboarding/start/${input.employeeId}`, {
        templateId: input.templateId ?? null,
        startDate: input.startDate ?? null,
      }),
    onSuccess: () => {
      // Employee queries too: starting a checklist is what the employee screen keys its
      // "onboarding in progress" state off.
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

export interface ActivationReadiness {
  employeeId: string;
  employeeLabel: string;
  currentStatus: string;
  ready: boolean;
  /** null only when work-auth could not be consulted at all — not the same as "authorised". */
  workAuth: { allowed?: boolean; reason?: string | null } | null;
  workAuthBlocking: boolean;
  checklistId: string | null;
  blockingSteps: OnboardingStepView[];
  /** Enforcement can be switched off in config; the blockers are still reported either way. */
  enforcementEnabled: boolean;
  message: string;
}

/**
 * Whether the person can be made active, WITHOUT attempting it.
 *
 * The activate route throws and names what is outstanding; this returns the same list so the
 * button can be disabled with the reason shown, rather than inviting a click that fails.
 */
export function useActivationReadiness(employeeId: string | null): UseQueryResult<ActivationReadiness> {
  return useQuery({
    queryKey: ['onboarding', 'readiness', employeeId],
    queryFn: () => api.get<ActivationReadiness>(`/onboarding/employee/${employeeId ?? ''}/readiness`),
    enabled: employeeId !== null,
    retry: false,
  });
}

export function useActivateEmployee(): ReturnType<
  typeof useMutation<unknown, unknown, { employeeId: string; effectiveFrom?: string; note?: string | null }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { employeeId: string; effectiveFrom?: string; note?: string | null }) =>
      api.post(`/onboarding/employee/${input.employeeId}/activate`, {
        ...(input.effectiveFrom === undefined ? {} : { effectiveFrom: input.effectiveFrom }),
        note: input.note ?? null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

export interface UploadStepDocumentInput {
  stepId: string;
  /** The step's own documentType — the server requires it explicitly, it does not infer it. */
  documentType: string;
  fileName: string;
  mimeType: string;
  /** Base64 WITHOUT a `data:` prefix; the server decodes and rejects an empty result. */
  contentBase64: string;
  expiresAt?: string | null;
}

/** Upload a document against a step. */
export function useUploadStepDocument(
  instanceId: string | null,
): ReturnType<typeof useMutation<unknown, unknown, UploadStepDocumentInput>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UploadStepDocumentInput) =>
      api.post(`/onboarding/${instanceId ?? ''}/step/${input.stepId}/document`, {
        documentType: input.documentType,
        fileName: input.fileName,
        mimeType: input.mimeType,
        contentBase64: input.contentBase64,
        expiresAt: input.expiresAt ?? null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

/**
 * Verify or reject an uploaded document.
 *
 * Rejection REQUIRES a reason of at least 5 characters — the server enforces it, and rightly:
 * it is the only thing the person is told, and "rejected" with no reason means they upload the
 * same file again. Verification optionally records an expiry, which is what drives the
 * re-verification alerts on documents that lapse.
 */
export function useReviewStepDocument(): ReturnType<
  typeof useMutation<unknown, unknown, { documentId: string; decision: 'verify' | 'reject'; reason?: string; expiresAt?: string | null }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    // The two paths are written out rather than interpolating the decision into the URL.
    // `/documents/${id}/${decision}` works at runtime but is invisible to `verify:reachable`,
    // which matches route literals — so a dynamic segment makes a wired route look unreachable
    // and hides real gaps in the report. It is also easier to grep for.
    mutationFn: (input: { documentId: string; decision: 'verify' | 'reject'; reason?: string; expiresAt?: string | null }) =>
      input.decision === 'reject'
        ? api.post(`/onboarding/documents/${input.documentId}/reject`, { reason: input.reason ?? '' })
        : api.post(`/onboarding/documents/${input.documentId}/verify`, { expiresAt: input.expiresAt ?? null }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    },
  });
}

export interface OnboardingTemplate {
  id: string;
  name: string;
  kind: string;
  version: number;
  isActive: boolean;
  description?: string | null;
  stepCount?: number;
  employmentTypes?: string[];
}

export function useOnboardingTemplates(): UseQueryResult<{ templates: OnboardingTemplate[] }> {
  return useQuery({
    queryKey: ['onboarding', 'templates'],
    queryFn: () => api.get<{ templates: OnboardingTemplate[] }>('/onboarding/templates'),
    retry: false,
  });
}

/** One template WITH its step definitions — what the editor loads to edit an existing template. */
export interface TemplateStepDetail {
  key: string;
  title: string;
  description?: string | null;
  assignedTo: TemplateStepInput['assignedTo'];
  assignedToEmployeeId?: string | null;
  documentType: string | null;
  dueOffsetDays: number | null;
  required: boolean;
  blocksActivation: boolean;
  dependsOnKeys?: string[];
  appliesWhen?: StepCondition | null;
  requiredWhen?: StepCondition | null;
}

export function useOnboardingTemplateDetail(
  id: string | null,
): UseQueryResult<{ template: OnboardingTemplate; steps: TemplateStepDetail[] }> {
  return useQuery({
    queryKey: ['onboarding', 'template', id],
    queryFn: () => api.get<{ template: OnboardingTemplate; steps: TemplateStepDetail[] }>(`/onboarding/templates/${id ?? ''}`),
    enabled: id !== null,
    retry: false,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Template authoring
//
// The product's premise is that an organisation configures its own onboarding. Until these
// hooks existed, `Templates.tsx` listed the four shipped checklists and called NONE of the five
// routes that change them — a viewer presented as an administration screen. An organisation
// whose onboarding differs from the shipped steps (a different background-check vendor, an
// equipment form, a client-specific NDA) had no way to say so.
//
// Two rules the API enforces and the UI has to respect:
//
//  · A step KEY is what running checklists reference. It must match /^[A-Z][A-Z0-9_]{1,48}$/
//    and must stay stable when the title changes — renaming a title is safe, renaming a key
//    orphans the step on every checklist already running.
//  · Editing the steps of a template that has running instances is refused with 409. The
//    answer is a NEW VERSION: running checklists keep the version they started on, new ones
//    get the new steps. That is why `createNewVersion` exists rather than a force flag.
// ─────────────────────────────────────────────────────────────────────────────

export type ChecklistKind = 'ONBOARDING' | 'OFFBOARDING';
export type StepAssignee = 'EMPLOYEE' | 'HR' | 'IT' | 'MANAGER' | 'FINANCE';

export type ConditionOperator =
  | 'EQUALS'
  | 'NOT_EQUALS'
  | 'IN'
  | 'NOT_IN'
  | 'GREATER_THAN'
  | 'GREATER_OR_EQUAL'
  | 'LESS_THAN'
  | 'LESS_OR_EQUAL'
  | 'IS_EMPTY'
  | 'IS_NOT_EMPTY'
  | 'CONTAINS'
  | 'STARTS_WITH';

/** One clause: an employee attribute compared to a value. */
export interface StepFieldCondition {
  field: string;
  operator: ConditionOperator;
  value: string | number | boolean | null | Array<string | number>;
}

/**
 * A condition over the EMPLOYEE, deciding whether a step applies (`appliesWhen`) or is required
 * (`requiredWhen`). `null` = unconditional — applies to everyone / use the static `required` flag.
 */
export interface StepCondition {
  operator: 'AND' | 'OR';
  conditions: StepFieldCondition[];
}

export interface TemplateStepInput {
  /** Stable identifier. A–Z, 0–9 and underscore, 2–49 chars, starting with a letter. */
  key: string;
  title: string;
  description?: string | null;
  required?: boolean;
  assignedTo?: StepAssignee;
  documentType?: string | null;
  /** Days from the start date. Negative is allowed — some steps are due before day one. */
  dueOffsetDays?: number | null;
  reminderDays?: number[];
  dependsOnKeys?: string[];
  blocksActivation?: boolean;
  automationAction?: string | null;
  /** When set, the step is added only to a matching employee's checklist; null = everyone. */
  appliesWhen?: StepCondition | null;
  /** When set, `required` is decided per employee; null = use the static `required` flag. */
  requiredWhen?: StepCondition | null;
  notes?: string | null;
}

export interface CreateTemplateInput {
  name: string;
  kind: ChecklistKind;
  employmentTypes?: string[];
  description?: string | null;
  defaultDurationDays?: number;
  isDefault?: boolean;
  steps: TemplateStepInput[];
}

export function useCreateOnboardingTemplate(): ReturnType<
  typeof useMutation<unknown, unknown, CreateTemplateInput>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTemplateInput) => api.post('/onboarding/templates', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding', 'templates'] });
    },
  });
}

export interface UpdateTemplateInput {
  id: string;
  name?: string;
  description?: string | null;
  employmentTypes?: string[];
  defaultDurationDays?: number;
  isDefault?: boolean;
  isActive?: boolean;
}

/** Metadata only. Steps go through `useReplaceTemplateSteps` or a new version. */
export function useUpdateOnboardingTemplate(): ReturnType<
  typeof useMutation<unknown, unknown, UpdateTemplateInput>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateTemplateInput) => api.patch(`/onboarding/templates/${id}`, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding', 'templates'] });
    },
  });
}

/** Replaces the whole step list. Refused with 409 when instances are running on this version. */
export function useReplaceTemplateSteps(): ReturnType<
  typeof useMutation<unknown, unknown, { id: string; steps: TemplateStepInput[] }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; steps: TemplateStepInput[] }) =>
      api.put(`/onboarding/templates/${input.id}/steps`, { steps: input.steps }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding', 'templates'] });
    },
  });
}

/** The answer to that 409. Running checklists keep the old version. */
export function useCreateTemplateVersion(): ReturnType<
  typeof useMutation<unknown, unknown, { id: string; steps?: TemplateStepInput[]; name?: string }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; steps?: TemplateStepInput[]; name?: string }) =>
      api.post(`/onboarding/templates/${input.id}/version`, {
        ...(input.steps === undefined ? {} : { steps: input.steps }),
        ...(input.name === undefined ? {} : { name: input.name }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding', 'templates'] });
    },
  });
}

/** Retire a template. Existing checklists are unaffected; it stops being selectable. */
export function useDeactivateOnboardingTemplate(): ReturnType<
  typeof useMutation<unknown, unknown, { id: string }>
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) => api.post(`/onboarding/templates/${input.id}/deactivate`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['onboarding', 'templates'] });
    },
  });
}
